import { useState } from 'react';
import { useAssignSector, useSectorCheckIn, type SectorAssignment } from '../api';
import RelativeTime, { SWEEP_SCALE } from './RelativeTime';
import { formatPhone } from '../phone';

/**
 * Who is responsible for this sector, and when they last checked in.
 *
 * Responsibility is recorded per layer, so which role this is depends on how
 * the board is cut: viewing by sector you are naming the lead, viewing by
 * walk you are naming whoever is knocking on the doors. They are different
 * people, and the packet prints both above the sector's map.
 *
 * The printed sector sheet has always had an "Assigned to ___" line; this is
 * where it gets filled so it prints already answered. Free text on purpose —
 * the people walking these streets are neighbors and runners, not Steward
 * users, and requiring them to be records would make the field unusable at
 * the exact moment it matters.
 *
 * Last-contact is shown with sweep-scale staleness tinting, because a sector
 * nobody has heard from in an hour is the thing a commander needs to notice,
 * and that silence cannot be inferred from the site rows — it is precisely
 * the absence of them.
 */
export default function SectorHeader({ incidentId, layer, sector, assignment }: {
  incidentId: string;
  layer: string;
  sector: string;
  assignment: SectorAssignment | undefined;
}) {
  const assign = useAssignSector();
  const checkIn = useSectorCheckIn();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(assignment?.assigned_to ?? '');
  const [phone, setPhone] = useState(assignment?.phone ?? '');
  const [radio, setRadio] = useState(assignment?.radio_channel ?? '');

  const unassignable = sector === 'Unassigned';
  // The words a person would use for the two layers the ward is cut on. An
  // unknown layer gets the neutral one rather than a wrong one.
  const role = layer === 'macro' ? 'leading' : layer === 'micro' ? 'walking' : 'taking';

  if (editing) {
    return (
      <form
        onClick={(e) => e.stopPropagation()}
        onSubmit={(e) => {
          e.preventDefault();
          assign.mutate(
            { incidentId, layer, sector, assigned_to: name.trim(),
              phone: phone.trim() || null, radio_channel: radio.trim() || null },
            { onSuccess: () => setEditing(false) },
          );
        }}
        className="flex flex-wrap items-center gap-1"
      >
        <input
          autoFocus value={name} onChange={(e) => setName(e.target.value)}
          placeholder={`Who is ${role} this?`}
          className="w-44 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
        />
        <input
          value={phone} onChange={(e) => setPhone(e.target.value)}
          placeholder="phone"
          className="w-28 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
        />
        <input
          value={radio} onChange={(e) => setRadio(e.target.value)}
          placeholder="radio"
          className="w-20 rounded border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 px-1.5 py-0.5 text-xs"
        />
        <button type="submit" disabled={assign.isPending}
          className="rounded bg-slate-800 dark:bg-slate-200 px-2 py-0.5 text-xs text-white dark:text-slate-900">
          Save
        </button>
        <button type="button" onClick={() => setEditing(false)} className="px-1 text-xs text-slate-400">✕</button>
      </form>
    );
  }

  if (!assignment?.assigned_to) {
    return unassignable ? null : (
      <button
        onClick={(e) => { e.stopPropagation(); setEditing(true); }}
        className="rounded border border-dashed border-slate-300 dark:border-slate-600 px-1.5 py-0.5 text-xs text-slate-400 hover:text-slate-700 dark:hover:text-slate-200"
        title={`Name who is ${role} this — it prints above the sector's map`}
      >
        + assign
      </button>
    );
  }

  return (
    <span className="flex items-center gap-1.5 text-xs" onClick={(e) => e.stopPropagation()}>
      <button
        onClick={() => setEditing(true)}
        className="font-medium text-slate-700 dark:text-slate-200 hover:underline"
        title={[formatPhone(assignment.phone),
                assignment.radio_channel && `radio ${assignment.radio_channel}`]
          .filter(Boolean).join(' · ') || 'Click to change'}
      >
        {assignment.assigned_to}
      </button>
      {assignment.radio_channel && (
        <span className="rounded bg-slate-100 dark:bg-slate-800 px-1 text-slate-500 dark:text-slate-400">
          {assignment.radio_channel}
        </span>
      )}
      <RelativeTime at={assignment.last_contact_at} thresholds={SWEEP_SCALE} never="no contact yet" />
      <button
        onClick={() => checkIn.mutate({ incidentId, layer, sector })}
        disabled={checkIn.isPending}
        className="rounded border border-slate-300 dark:border-slate-600 px-1.5 py-0.5 text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-700"
        title="Stamp a check-in from this sector now"
      >
        Check in
      </button>
    </span>
  );
}
